import isEqual from 'deep-equal';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useConfig } from 'shared/config/ConfigContext';
import type { DeviceType } from 'shared/types';
import { setInitialBannerFilterValues } from 'shared/util/bannerParams';
import { queryParamToString } from 'shared/util/query';
import { FhhBreadcrumbs } from '@/components/fhhBreadcrumbs/FhhBreadcrumbs';
import type { FhhState, Filters, ResultData } from '@/domain';
import * as resultActions from '@/ducks/results/resultsActions';
import { useSearchResultsWithPricesRegular } from '@/hooks/useSearchResultsWithPricesRegular';
import { trackResultPage } from '@/lib/tracking/tracking';
import { FhhSearch } from './components/newSearch/FhhSearch';
import { SearchDetails } from './components/newSearch/SearchDetails';
import { ResultFilter } from './components/resultFilter/ResultFilter';
import { ResultList } from './components/resultList/ResultList';
import { TopBar } from './components/topBar/TopBar';

export interface ResultPageProps {
    deviceType: DeviceType;
    loginId: number;
    searchId: string | null;
}

const hasSearchParams = (filters: Filters) =>
    Boolean(
        (filters.city && filters.city?.length > 0) ||
            (filters.country && filters.country?.length > 0) ||
            (filters.booking_from && filters.booking_from?.length > 0 && filters.booking_to && filters.booking_to?.length > 0) ||
            (filters.no_of_bedrooms_from && filters.no_of_bedrooms_from > 0) ||
            (filters.no_of_beds_from && filters.no_of_beds_from > 0),
    );

export const ResultPage = ({ deviceType, loginId, searchId }: ResultPageProps) =>
    searchId ? (
        <WithExactPrices deviceType={deviceType} loginId={loginId} searchId={searchId} />
    ) : (
        <WithBasePrices deviceType={deviceType} loginId={loginId} />
    );

const WithExactPrices = ({ deviceType, loginId, searchId }: ResultPageProps) => {
    const { filters } = useSelector((state: FhhState) => state.results);
    // @ts-ignore searchId can be null... please fix.
    const { data, loading } = useSearchResultsWithPricesRegular(searchId, filters);
    return <ResultPageView deviceType={deviceType} loginId={loginId} data={data} loading={loading} />;
};

const WithBasePrices = ({ deviceType, loginId }: Omit<ResultPageProps, 'searchId'>) => {
    const { apiExternalUrl } = useConfig();
    const router = useRouter();
    const dispatch = useDispatch();

    const { data, filters } = useSelector((state: FhhState) => state.results);

    useEffect(() => {
        populateResultsIfEmpty();
    }, []);

    const populateResultsIfEmpty = () => {
        const query = router?.query || {};

        if (data.resultItems === null) {
            dispatch(resultActions.populateResults(apiExternalUrl, Object.assign({}, filters, query))); // this is the trigger for the default search
        }
    };

    return <ResultPageView deviceType={deviceType} loginId={loginId} data={data} loading={false} />;
};

interface ResultPageViewProps {
    deviceType: DeviceType;
    loginId: number;
    data: ResultData;
    loading: boolean;
}

const ResultPageView = ({ deviceType, loginId, data, loading }: ResultPageViewProps) => {
    const { apiExternalUrl } = useConfig();
    const router = useRouter();
    const dispatch = useDispatch();

    const { filters, filterData, autocompleteData } = useSelector((state: FhhState) => state.results);

    const [openSearchForm, setOpenSearchForm] = useState(false);
    const showSearchForm = openSearchForm || !hasSearchParams(filters);

    useEffect(() => {
        if (!isFalsyOrEmpty(router?.query)) {
            dispatch(resultActions.updateFilters({ ...router.query, nrFUSAds: deviceType.isProbablyMobile ? 1 : 2 }));
        }

        setInitialBannerFilterValues({
            pageType: 'result',
            page: filters.page ?? undefined,
            destination: {
                city: autocompleteData?.trackingInfo?.city || filters.city || queryParamToString(router?.query?.city),
                country: autocompleteData?.trackingInfo?.country || filters.country || queryParamToString(router?.query?.country),
            },
        });
    }, []);

    const prevFilters = useRef<Filters>(filters);
    const prevData = useRef<ResultData>();
    useEffect(() => {
        const unchangedFilters = isEqual(filters, prevFilters.current);

        if (unchangedFilters && isEqual(data, prevData.current)) {
            return;
        }
        prevFilters.current = filters;
        prevData.current = data;
        // On a page load, the stats object is empty. This object is needed for tracking, so must wait until
        // next update when stats is set before tracking. After that, track on every filter change.
        if (data.stats?.numResults !== undefined) {
            trackResultPage('Grid', { data, filters });
        }

        if (unchangedFilters) {
            return;
        }
        router?.replace({ pathname: router.pathname, query: filters }, undefined, { shallow: true });
    }, [filters, data.stats]);

    const isFalsyOrEmpty = (value: unknown) =>
        !value || (Object.prototype.toString.call(value) === '[object Object]' && JSON.stringify(value) === '{}');

    const filterResults = (filter: Filters) => dispatch(resultActions.filterResults(apiExternalUrl, filter));

    return (
        <div data-testid="resultPage">
            <FhhBreadcrumbs className="hidden md:block" />
            {showSearchForm ? (
                <FhhSearch filters={filters} openSearchForm={() => setOpenSearchForm(false)} />
            ) : (
                <SearchDetails filters={filters} openSearchForm={() => setOpenSearchForm(true)} />
            )}
            <section className="grid md:grid-cols-3 gap-32">
                <div className="hidden md:block" data-testid="Sidebar-desktop">
                    <ResultFilter filters={filters} filterData={filterData} onFilterChanged={filterResults} />
                </div>
                <div className="md:col-span-2 mb-144">
                    <TopBar
                        onFilterChanged={filterResults}
                        filters={filters}
                        filterData={filterData}
                        autocompleteData={autocompleteData}
                        isLoggedIn={loginId != null}
                    />
                    <ResultList
                        results={data.resultItems}
                        stats={data.stats}
                        filters={filters}
                        onFilterChanged={filterResults}
                        numberOfPreloadedImages={6}
                        loginId={loginId}
                        loading={loading}
                    />
                </div>
            </section>
        </div>
    );
};
